import React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import default_theme from "assets/themes/themes";
import {
  CONTROLLER_NAME,
  LEGAL_LAST_UPDATED,
  PRIVACY_CONTACT_EMAIL,
} from "./legalContentConstants";
import styles from "./LegalPage.module.css";

// This theme sets palette.primary.main to white, so MUI Links (which default
// to color="primary") would otherwise render white-on-white. Use the brand teal.
const LINK_COLOR = default_theme.palette.other.main;

/**
 * Shared shell for every legal document.
 *
 * @param {string} title        Page heading.
 * @param {string} [intro]      Optional short introduction paragraph.
 * @param {Array<{id:string,title:string}>} [sections]  Table-of-contents entries.
 * @param {string} [lastUpdated] Overrides the default "last updated" value.
 * @param {React.ReactNode} children  The document body (usually <LegalSection>s).
 */
const LegalPage = ({
  title,
  intro,
  sections = [],
  lastUpdated = LEGAL_LAST_UPDATED,
  children,
}) => {
  const { pathname } = useLocation();
  // The legal index IS the hub, so don't show a "back to all documents" link on it.
  const isLegalHub = pathname.replace(/\/+$/, "") === "/legal";

  return (
    <Box className={styles.pageContainer}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Last updated: {lastUpdated}
          </Typography>

          {intro && (
            <Typography variant="body1" className={styles.prose} sx={{ mb: 2 }}>
              {intro}
            </Typography>
          )}

          {sections.length > 0 && (
            <Box
              component="nav"
              className={styles.toc}
              aria-label="Table of contents"
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                On this page
              </Typography>
              <Box component="ol" className={styles.tocList}>
                {sections.map((section) => (
                  <li key={section.id}>
                    <MuiLink href={`#${section.id}`} underline="hover" sx={{ color: LINK_COLOR }}>
                      {section.title}
                    </MuiLink>
                  </li>
                ))}
              </Box>
            </Box>
          )}

          <Box className={styles.prose}>{children}</Box>

          <Box className={styles.contactBox}>
            <Typography variant="body2" color="text.secondary">
              Questions about this document? Contact{" "}
              <MuiLink href={`mailto:${PRIVACY_CONTACT_EMAIL}`} sx={{ color: LINK_COLOR }}>
                {PRIVACY_CONTACT_EMAIL}
              </MuiLink>
              . Data controller: {CONTROLLER_NAME}.
            </Typography>
          </Box>

          {!isLegalHub && (
            <Box sx={{ mt: 3 }}>
              <MuiLink component={RouterLink} to="/legal" underline="hover" sx={{ color: LINK_COLOR }}>
                ← All legal documents
              </MuiLink>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default LegalPage;
